///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The earthquake cutout: a block of crust lifted out of the globe above a
/// focused earthquake. Its lid is the local terrain, its interior the
/// sequence's hypocenters at depth, and its two far walls are the
/// cross-sections (hypocenters projected as shadows, depth ticks tinted
/// with the depth ramp). The block is oriented along the strike of the
/// sequence so the end wall is the canonical dip section.
///
/// Block frame: x along strike, y up, z across strike, km. The node origin
/// sits at the center of the box at mid depth (what the camera orbits); the
/// `frame` child shifts so that y = 0 is sea level for every part.
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { TextNode } from "@/earthbrowser/entities/TextNode/TextNode"
import { type Font } from "@/earthbrowser/libs/opentype"
import { FontCache } from "@/earthbrowser/utils/FontCache"
import { geographic } from "@/earthbrowser/utils/geographic"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type Camera } from "@stovokor/camera/Camera"
import { CameraState } from "@stovokor/camera/CameraState"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { animation, easing } from "@stovokor/math/animation"
import { rgba } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { cross, dot, normal, vec3 } from "@stovokor/math/vector"
import { type QuakeSequence } from "./QuakeSequenceQuery"
import { boxDepthKm, clamp, toBlockXZ, type CutoutLayout } from "./cutoutLayout"
import { type CutoutElevation } from "./CutoutElevation"
import { CutoutTerrainNode } from "./CutoutTerrainNode"
import { CutoutWallsNode, type FarWalls } from "./CutoutWallsNode"
import { FaultPlanesNode, type FaultPlaneSummary } from "./FaultPlanesNode"
import { GhostCloudNode, HypocenterCloudNode } from "./HypocenterCloudNode"
import { type QuakeHistory } from "./QuakeHistoryQuery"
import { type Slab2Facts, type Slab2Surface } from "./Slab2Query"
import { type ExposureSummary } from "./ExposureQuery"
import { SlabSurfaceNode } from "./SlabSurfaceNode"
import { bValue, bathExpectedLargest, fitRupturePlane, omoriCheck, type BValueEstimate, type OmoriCheck, type PlaneFit } from "./sequenceStats"
import { type AftershockForecast, type UsgsEventDetail } from "./UsgsDetail"

/** Statistics and geometry derived from the sequence, for the panel */
export interface CutoutAnalysis {
	bValue?: BValueEstimate
	/** Båth's law expectation for the largest aftershock */
	bathExpected: number
	largestAftershock?: number
	omori?: OmoriCheck
	planes?: FaultPlaneSummary
	forecast?: AftershockForecast
}

const cLabelFont = "/fonts/Inter-Regular.otf"
/** Default view: elevation above the block and azimuth off the across-strike axis */
const cDefaultTiltFromNadir = 55 * trig.rad
const cDefaultAzimuth = 35 * trig.rad

const cEdgeColor = rgba(0.80, 0.86, 0.94, 0.30)
const cPlumbColor = rgba(1, 1, 1, 0.55)
const cPlumbGroundColor = rgba(1, 1, 1, 0.28)
const cLabelColor = rgba(0.86, 0.90, 0.96, 1)
const cLabelDimColor = rgba(0.62, 0.68, 0.76, 1)

export class QuakeCutoutNode extends GestureNode implements InfoPanelProvider {
	public readonly focusable: boolean = true
	public readonly id: string

	public readonly sequence: QuakeSequence
	public readonly layout: CutoutLayout

	private readonly cutoutContext: EarthBrowserContext
	private readonly cameraAdapter: EntityCameraAdapter
	private readonly frame: NodeBase
	private readonly walls: CutoutWallsNode
	private readonly cloud: HypocenterCloudNode
	private terrain?: CutoutTerrainNode
	private ghosts?: GhostCloudNode
	public history?: QuakeHistory
	private planes?: FaultPlanesNode
	private slab?: SlabSurfaceNode
	public slabFacts?: Slab2Facts
	public exposure?: ExposureSummary
	private readonly planeFit?: PlaneFit
	/** Tsunami travel time is baked into the lid for offshore mainshocks of M ≥ 6.5 */
	private readonly tsunamiEligible: boolean
	/** Undefined when the quake isn't a mainshock (statistics would be meaningless) */
	public readonly analysis?: CutoutAnalysis
	private readonly analysisEnabled: boolean
	private readonly labels: NodeBase
	private depthLabels: TextNode[] = []
	private infoLabel?: TextNode
	private font?: Font

	private readonly hitBox: cube
	private readonly inverseTransform: matrix
	private readonly boxDepth: number
	private readonly mainshockX: number
	private readonly mainshockZ: number
	private lidTopKm = 0
	private far: FarWalls = { signX: 1, signZ: 1 }
	private farApplied = false
	private revealAnim?: animation
	private terrainAnim?: animation
	private ghostAnim?: animation
	private planeAnim?: animation
	private slabAnim?: animation
	private lastCameraLocal?: vec3
	private destroyed = false

	/** Fired when focus leaves the cutout for something else (user clicked away) */
	public onDismissed?: () => void

	constructor(context: EarthBrowserContext, sequence: QuakeSequence, layout: CutoutLayout, analysisEnabled: boolean) {
		super(context)
		this.cutoutContext = context
		this.sequence = sequence
		this.layout = layout
		this.id = `quake-cutout-${sequence.mainshock.id}`
		this.boxDepth = boxDepthKm(layout)

		this.transform = this.buildWorldTransform()
		this.inverseTransform = this.transform.invert()

		// Everything below is built with y = 0 at sea level
		this.frame = new NodeBase()
		this.frame.transform = matrix.translate(0, this.boxDepth / 2, 0)
		this.addNode(this.frame)

		this.walls = new CutoutWallsNode(context, layout)
		this.frame.addNode(this.walls)

		const main = sequence.mainshock
		const mainshockTimeMs = Date.parse(main.event_time)
		this.cloud = new HypocenterCloudNode(context, layout, sequence.events, main.id, mainshockTimeMs)
		this.frame.addNode(this.cloud)

		const mxz = toBlockXZ(-layout.centerEast, -layout.centerNorth, layout.strike)
		this.mainshockX = mxz.x
		this.mainshockZ = mxz.z
		this.buildEdges()
		this.buildPlumbLine()

		// Sequence statistics, only when this quake is the mainshock of its cluster
		this.analysisEnabled = analysisEnabled
		this.tsunamiEligible = analysisEnabled && main.mag >= 6.5
		if(analysisEnabled) {
			const mags = sequence.events.filter(e => e.inSequence && e.id !== main.id).map(e => e.mag)
			this.planeFit = fitRupturePlane(sequence.events, layout)
			this.analysis = {
				bValue: bValue(mags),
				bathExpected: bathExpectedLargest(main.mag),
				largestAftershock: sequence.largestAftershockMag,
				omori: omoriCheck(sequence.events, mainshockTimeMs, Date.now())
			}
		}

		this.labels = new NodeBase()
		this.frame.addNode(this.labels)
		void FontCache.load(cLabelFont).then(font => {
			if(this.destroyed) return
			this.font = font
			this.buildLabels()
			this.planes?.setFont(font)
			this.farApplied = false
		})

		const halfX = layout.lengthKm / 2
		const halfZ = layout.widthKm / 2
		this.hitBox = new cube()
		this.hitBox.update(-halfX, -this.boxDepth / 2, -halfZ)
		this.hitBox.update(halfX, this.boxDepth / 2, halfZ)

		const extent = Math.max(layout.lengthKm, layout.widthKm, this.boxDepth * 1.6)
		const focusDistance = clamp(2.1 * extent, 80, 3000)
		this.cameraAdapter = new EntityCameraAdapter(context, this, focusDistance, Math.max(500, extent * 6))

		this.revealAnim = new animation()
			.from(0).to(1).duration(0.9).easing(easing.quadraticOut)
			.onUpdate(v => {
				this.walls.opacity = v
				this.cloud.reveal = v
				this.cloud.opacity = v
			})
		this.revealAnim.start()
	}

	/**
	 * USGS detail products arrive after their own fetch (or fail to): build
	 * the fault-plane sheets from the aftershock fit plus the moment-tensor
	 * nodal planes, and keep the official aftershock forecast for the panel
	 */
	public setDetail(detail: UsgsEventDetail | undefined): void {
		if(this.destroyed || this.planes || !this.analysis || !this.analysisEnabled) return
		this.analysis.forecast = detail?.forecast
		const main = this.sequence.mainshock
		const planes = new FaultPlanesNode(this.cutoutContext, this.layout,
			{ x: this.mainshockX, depthKm: main.depth_km, z: this.mainshockZ, mag: main.mag },
			this.planeFit, detail?.momentTensor, this.labelSizeKm)
		this.analysis.planes = planes.summary
		if(!planes.hasContent) {
			planes.destroy()
			return
		}
		this.planes = planes
		this.frame.addNode(planes)
		if(this.font) planes.setFont(this.font)
		this.farApplied = false
		this.planeAnim = new animation()
			.from(0).to(1).duration(0.8).easing(easing.quadraticOut)
			.onUpdate(v => { planes.opacity = v })
		this.planeAnim.start()
	}

	/** Terrain arrives after the elevation tiles; it rises out of the sea-level plane */
	public setTerrain(elevation: CutoutElevation): void {
		if(this.destroyed || this.terrain) return
		const main = this.sequence.mainshock
		const tsunamiSource = this.tsunamiEligible ? { x: this.mainshockX, z: this.mainshockZ } : undefined
		this.terrain = new CutoutTerrainNode(this.cutoutContext, this.layout, elevation, main.lat, main.lon, tsunamiSource)
		this.frame.addNode(this.terrain)
		this.lidTopKm = this.terrain.maxElevKm * this.terrain.exaggeration
		this.hitBox.update(0, this.boxDepth / 2 + this.lidTopKm, 0)
		this.updateInfoLabel()

		const terrain = this.terrain
		this.terrainAnim = new animation()
			.from(0).to(1).duration(1.1).easing(easing.quadraticInOut)
			.onUpdate(v => { terrain.reveal = v })
		this.terrainAnim.start()
	}

	/** Tsunami front arrival at the block edge, minutes after the mainshock (offshore mainshocks only) */
	public get tsunamiEdgeArrivalMin(): number | undefined {
		const sec = this.terrain?.tsunamiEdgeArrivalSec
		return sec === undefined ? undefined : sec / 60
	}

	public get tsunamiIsochronMin(): number {
		return this.terrain?.isochronMin ?? 10
	}

	/** Slab2 interface arrives after its own query; drawn when it reaches into the box */
	public setSlab(surface: Slab2Surface): void {
		if(this.destroyed || this.slab) return
		this.slabFacts = surface.facts
		const main = this.sequence.mainshock
		const node = new SlabSurfaceNode(this.cutoutContext, this.layout, surface, main.lat, main.lon)
		if(node.vertexCount === 0) {
			node.destroy()
			return
		}
		this.slab = node
		this.frame.addNode(node)
		this.slabAnim = new animation()
			.from(0).to(1).duration(0.8).easing(easing.quadraticOut)
			.onUpdate(v => { node.opacity = v })
		this.slabAnim.start()
	}

	public get slabDrawn(): boolean {
		return this.slab !== undefined
	}

	/** Historical catalog arrives after its own query; ghosts fade in behind the sequence */
	public setHistory(history: QuakeHistory): void {
		if(this.destroyed || this.history) return
		this.history = history
		if(history.ghosts.length === 0) return
		const main = this.sequence.mainshock
		this.ghosts = new GhostCloudNode(this.cutoutContext, this.layout, history.ghosts, Date.parse(main.event_time))
		this.frame.addNode(this.ghosts)
		const ghosts = this.ghosts
		this.ghostAnim = new animation()
			.from(0).to(1).duration(0.8).easing(easing.quadraticOut)
			.onUpdate(v => {
				ghosts.reveal = v
				ghosts.opacity = v
			})
		this.ghostAnim.start()
	}

	/** Land exaggeration, when there is land in the footprint to apply it to */
	public get terrainExaggeration(): number | undefined {
		const t = this.terrain
		return t && t.maxElevKm > 0.05 ? t.exaggeration : undefined
	}

	/**
	 * Sea-floor exaggeration when bathymetry covers the footprint and it
	 * isn't already implied by the land factor
	 */
	public get seaFloorExaggeration(): number | undefined {
		const t = this.terrain
		if(!t || t.maxDepthKm <= 0.05) return undefined
		if(this.terrainExaggeration !== undefined && t.seaExaggeration === t.exaggeration) return undefined
		return t.seaExaggeration
	}

	public get hasBathymetry(): boolean {
		return (this.terrain?.maxDepthKm ?? 0) > 0.05
	}

	public get ghostCount(): number {
		return this.ghosts?.eventCount ?? 0
	}

	public get eventCount(): number {
		return this.cloud.eventCount
	}

	// ========== Frame ==========

	/**
	 * World placement: tangent frame at the mainshock epicenter rotated to
	 * the strike, translated to the block center and lifted so the box
	 * bottom clears the surface by hoverKm
	 */
	private buildWorldTransform(): matrix {
		const main = this.sequence.mainshock
		const layout = this.layout
		const epicenter = new geographic(main.lon * trig.rad, main.lat * trig.rad, 0).cartesian
		const up = normal(epicenter)
		const north = normal(vec3.unitY.subtract(up.multiply(dot(vec3.unitY, up))))
		const east = cross(north, up)
		const s = Math.sin(layout.strike)
		const c = Math.cos(layout.strike)
		const xAxis = east.multiply(s).add(north.multiply(c))
		const zAxis = east.multiply(c).subtract(north.multiply(s))
		const origin = epicenter
			.add(east.multiply(layout.centerEast))
			.add(north.multiply(layout.centerNorth))
			.add(up.multiply(layout.hoverKm + this.boxDepth / 2))
		return new matrix([
			xAxis.x, xAxis.y, xAxis.z, 0,
			up.x, up.y, up.z, 0,
			zAxis.x, zAxis.y, zAxis.z, 0,
			origin.x, origin.y, origin.z, 1
		])
	}

	// ========== Static dressing ==========

	private buildEdges(): void {
		const hx = this.layout.lengthKm / 2
		const hz = this.layout.widthKm / 2
		const d = -this.boxDepth
		const line = (positions: vec3[], loop: boolean): void => {
			const node = new PolylineNode(this.cutoutContext, { positions, loop, width: 1.2, color: cEdgeColor, depthWrite: false })
			node.renderOrder = 3
			this.frame.addNode(node)
		}
		line([new vec3(-hx, 0, -hz), new vec3(hx, 0, -hz), new vec3(hx, 0, hz), new vec3(-hx, 0, hz)], true)
		line([new vec3(-hx, d, -hz), new vec3(hx, d, -hz), new vec3(hx, d, hz), new vec3(-hx, d, hz)], true)
		for(const [x, z] of [[-hx, -hz], [hx, -hz], [hx, hz], [-hx, hz]]) {
			line([new vec3(x, 0, z), new vec3(x, d, z)], false)
		}
	}

	/**
	 * Plumb line through the mainshock: hypocenter up to its epicenter on
	 * the lid, and from the box bottom down to the real ground, tying the
	 * floating block back to the place it was lifted from
	 */
	private buildPlumbLine(): void {
		const main = this.sequence.mainshock
		const x = this.mainshockX
		const z = this.mainshockZ
		const hypoY = -Math.min(main.depth_km, this.layout.depthFloorKm) * this.layout.depthScale
		const inner = new PolylineNode(this.cutoutContext, {
			positions: [new vec3(x, hypoY, z), new vec3(x, 0.01, z)],
			width: 1.5, color: cPlumbColor, depthWrite: false
		})
		inner.renderOrder = 3
		this.frame.addNode(inner)

		const groundElevKm = this.cutoutContext.displayElevationKm(new geographic(main.lon * trig.rad, main.lat * trig.rad, 0))
		// Sea level of the block sits hoverKm + boxDepth above the ellipsoid at the epicenter
		const groundY = -(this.layout.hoverKm + this.boxDepth) + groundElevKm
		const outer = new PolylineNode(this.cutoutContext, {
			positions: [new vec3(x, -this.boxDepth, z), new vec3(x, groundY, z)],
			width: 1.2, color: cPlumbGroundColor, depthWrite: false
		})
		outer.renderOrder = 3
		this.frame.addNode(outer)
	}

	// ========== Labels ==========

	private get labelSizeKm(): number {
		return clamp(0.028 * this.layout.lengthKm, 1.2, 25)
	}

	private buildLabels(): void {
		if(!this.font) return
		const layout = this.layout
		const ticks = Math.round(layout.depthFloorKm / layout.tickKm)
		for(let k = 0; k <= ticks; k++) {
			const km = k * layout.tickKm
			const node = new TextNode(this.cutoutContext, `${km} km`, this.font, this.labelSizeKm)
			node.color = k === 0 ? cLabelColor : cLabelDimColor
			this.labels.addNode(node)
			this.depthLabels.push(node)
		}
		this.updateInfoLabel()
		this.farApplied = false
	}

	private updateInfoLabel(): void {
		if(!this.font) return
		const parts: string[] = []
		if(this.layout.depthScale > 1) parts.push(`Depth ×${this.layout.depthScale}`)
		const terrainExag = this.terrainExaggeration
		const seaExag = this.seaFloorExaggeration
		if(terrainExag !== undefined) parts.push(`Terrain ×${terrainExag}`)
		if(seaExag !== undefined) parts.push(`Sea floor ×${seaExag}`)
		parts.push(`Grid ${this.layout.tickKm} km`)
		const text = parts.join("   ·   ")
		if(this.infoLabel) {
			this.infoLabel.text = text
		} else {
			this.infoLabel = new TextNode(this.cutoutContext, text, this.font, this.labelSizeKm * 0.8)
			this.infoLabel.color = cLabelDimColor
			this.labels.addNode(this.infoLabel)
		}
		this.farApplied = false
	}

	/**
	 * Lay the labels onto the far z wall next to the far corner, facing the
	 * camera side. Text geometry is centered on its origin in its own xy
	 * plane; `wallText` maps that plane onto the wall
	 */
	private layoutLabels(): void {
		if(!this.font) return
		const layout = this.layout
		const size = this.labelSizeKm
		const hx = layout.lengthKm / 2
		const hz = layout.widthKm / 2
		const sx = this.far.signX
		const sz = this.far.signZ
		// Far z wall plane, a hair toward the camera
		const wallZ = -sz * hz + sz * size * 0.05
		// Text runs along x, inward from the far corner
		const inward = sx

		this.depthLabels.forEach((node, k) => {
			const text = node.text
			const width = text.length * size * 0.56
			const x = -sx * hx + inward * (width / 2 + size * 0.5)
			const y = -k * layout.tickKm * layout.depthScale + size * 0.55
			node.transform = wallText(sz, new vec3(x, y, wallZ))
		})

		if(this.infoLabel) {
			const y = this.lidTopKm + size * 1.1
			this.infoLabel.transform = wallText(sz, new vec3(0, y, wallZ))
		}
	}

	// ========== Per frame ==========

	override render(state: RenderState): RenderActionSet[] {
		const clockMs = this.cutoutContext.clock.clockTime.date.getTime()
		this.cloud.setClock(clockMs)
		this.terrain?.setTsunamiClock((clockMs - Date.parse(this.sequence.mainshock.event_time)) / 1000)

		// Far walls follow the camera; a sign flip re-lays the labels
		const camLocal = this.inverseTransform.multVec3(state.camera.position, 1)
		const signX = camLocal.x >= 0 ? 1 : -1
		const signZ = camLocal.z >= 0 ? 1 : -1
		if(!this.farApplied || signX !== this.far.signX || signZ !== this.far.signZ) {
			this.far = { signX, signZ }
			this.farApplied = true
			this.walls.setFarWalls(this.far)
			this.cloud.setFarWalls(this.far)
			this.layoutLabels()
			this.lastCameraLocal = undefined
		}

		// Fault-plane labels face the camera side of each sheet; re-lay when the camera moves
		if(this.planes) {
			const frameLocal = new vec3(camLocal.x, camLocal.y - this.boxDepth / 2, camLocal.z)
			const last = this.lastCameraLocal
			if(!last || last.subtract(frameLocal).length > 0.01 * this.layout.lengthKm) {
				this.lastCameraLocal = frameLocal
				this.planes.layoutLabels(frameLocal)
			}
		}

		return super.render(state)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.hitBox.intersect(state.ray)
		if(dist > 0) {
			return { node: this, hitPoint: state.ray.pointAt(dist), distance: dist }
		}
		return undefined
	}

	// ========== Focus lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
	}

	/**
	 * An info-panel provider with no panel: the HUD replaces the anchored
	 * card, and returning undefined makes the focus plumbing hide the
	 * callout without closing the quake's info window or clearing the URL
	 * target (which would deselect the quake and dismiss this block)
	 */
	public getInfoPanelData(): InfoPanelData | undefined {
		return undefined
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.onDismissed?.()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	/**
	 * Fly to an oblique three-quarter view: elevated enough to read the lid,
	 * low enough to see the hypocenters against the far walls, approaching
	 * from whichever quadrant the camera is already in
	 */
	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.defaultCameraState(camera),
			params: { duration: 0.8, easing: "easeInOut" }
		}
	}

	private defaultCameraState(camera: Camera): CameraState {
		const camLocal = this.inverseTransform.multVec3(camera.position, 1)
		const sx = camLocal.x >= 0 ? 1 : -1
		const sz = camLocal.z >= 0 ? 1 : -1
		const sinT = Math.sin(cDefaultTiltFromNadir)
		const cosT = Math.cos(cDefaultTiltFromNadir)
		const dirLocal = new vec3(
			sinT * Math.sin(cDefaultAzimuth) * sx,
			cosT,
			sinT * Math.cos(cDefaultAzimuth) * sz
		)
		const distance = this.cameraAdapter.getTargetCameraState(camera).position.subtract(this.transform.position).length
		const dirWorld = normal(this.transform.multVec3(dirLocal, 0))
		const origin = this.transform.position
		const position = origin.add(dirWorld.multiply(distance))
		const forward = normal(origin.subtract(position))
		const upHint = normal(origin)
		const up = normal(upHint.subtract(forward.multiply(dot(upHint, forward))))
		return new CameraState(position, forward, up)
	}

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	override destroy(): void {
		this.destroyed = true
		this.revealAnim?.stop()
		this.terrainAnim?.stop()
		this.ghostAnim?.stop()
		this.planeAnim?.stop()
		this.slabAnim?.stop()
		this.onDismissed = undefined
		super.destroy()
	}
}

/**
 * Transform placing text (centered, in its own xy plane) on a z wall at
 * `at`, running along +x or -x so it reads left-to-right from the camera
 * side, upright, with its face toward the camera. signZ is the camera's
 * side of the wall
 */
function wallText(signZ: number, at: vec3): matrix {
	// TextGeometry reads correctly from its -z side with its +y pointing
	// down; a proper rotation (no reflection) keeps the glyphs unmirrored
	const right = new vec3(signZ, 0, 0)
	const up = new vec3(0, -1, 0)
	const face = new vec3(0, 0, -signZ)
	return new matrix([
		right.x, right.y, right.z, 0,
		up.x, up.y, up.z, 0,
		face.x, face.y, face.z, 0,
		at.x, at.y, at.z, 1
	])
}
